import * as google_protobuf_wrappers_pb from 'google-protobuf/google/protobuf/wrappers_pb';

import request from '@api/base/request';
import authSharedService from '@api/services/authSharedService';
import {
  Attribution,
  AttributionTouch,
  CodeType,
  Pair,
  RealmType,
  RegistrationCode,
} from 'respona_api/generated/common_pb';
import {
  getAttribution,
  getAttributionUtmPairs,
  AttributionTouch as AttributionTouchData,
} from '@helpers/attribution';
import * as google_protobuf_empty_pb from 'google-protobuf/google/protobuf/empty_pb';
import { ReCaptchaShowStatusType } from '@ts/apiModels.types';
import {
  AppSumoTokensResponseType,
  LoginResponseType,
  RefCodeTypes,
  VerificationEmailResponseType,
} from '@ts/auth.types';
import AuthSharedModel, {
  AuthHandlerType,
  AuthHandlerTypeMap,
  CheckAccountRequest,
  VerificationCodeTypeMap,
} from 'respona_api/generated/auth-shared_pb';
import { getRealm } from '@utils/realmIdentifier';
import * as common_pb from 'respona_api/generated/common_pb';

export const getTimeZone = () => {
  const timeZone = -(new Date().getTimezoneOffset() / 60);

  const UTCSign = timeZone < 0 ? '-' : '+';
  const timezoneAbs = Math.abs(timeZone);
  const formattedTimeZone = timezoneAbs < 10 ? `0${timezoneAbs}` : `${timezoneAbs}`;

  return `UTC${UTCSign}${formattedTimeZone}`;
};

export const checkAccountByEmailApi = (email: string): Promise<VerificationEmailResponseType> => {
  const client = authSharedService.getInstance();
  const checkRequest = new CheckAccountRequest();

  checkRequest.setEmail(email);
  checkRequest.setRealm(getRealm());

  return request<VerificationEmailResponseType>(
    client,
    client.checkAccountByEmail,
    checkRequest,
    false
  );
};

export const loginFromSemrush = (uid: string, password: string): Promise<LoginResponseType> => {
  const client = authSharedService.getInstance();
  const loginRequest = new AuthSharedModel.LoginRequest();

  loginRequest.setToken(uid);
  if (password != null) {
    loginRequest.setOptionalPassword(password);
  }
  loginRequest.setRealm(RealmType.SEMRUSH);
  loginRequest.setDestinationRealm(RealmType.SEMRUSH);
  loginRequest.setType(AuthHandlerType.SEMRUSH);
  loginRequest.setUtcTimeZone(getTimeZone());

  return request<LoginResponseType>(client, client.login, loginRequest, false);
};

export const loginByEmailApi = (
  email: string,
  password: string,
  captcha?: string
): Promise<LoginResponseType> => {
  const client = authSharedService.getInstance();
  const loginRequest = new AuthSharedModel.LoginRequest();

  loginRequest.setToken(email);
  loginRequest.setOptionalPassword(password);
  loginRequest.setRealm(getRealm());
  loginRequest.setDestinationRealm(getRealm());
  loginRequest.setType(AuthHandlerType.EMAIL);
  loginRequest.setUtcTimeZone(getTimeZone());

  if (captcha) {
    loginRequest.setOptionalReCaptchaResponse(captcha);
  }

  return request<LoginResponseType>(client, client.login, loginRequest, false);
};

export const loginForUser = (
  token: string,
  realm: common_pb.RealmTypeMap[keyof common_pb.RealmTypeMap],
  type: AuthHandlerTypeMap[keyof AuthHandlerTypeMap]
): Promise<LoginResponseType> => {
  const client = authSharedService.getInstance();
  const loginRequest = new AuthSharedModel.LoginRequest();

  loginRequest.setToken(token);
  loginRequest.setRealm(realm);
  loginRequest.setDestinationRealm(getRealm());
  loginRequest.setType(type);
  loginRequest.setUtcTimeZone(getTimeZone());

  // This is the Microsoft (and other token-based) OAuth path, which also handles
  // first-time signups. Only meaningful on the register path; the backend ignores
  // it on plain login (QA 2026-08-28, F3).
  const attribution = composeAttribution();
  if (attribution) {
    loginRequest.setOptionalAttribution(attribution);
  }

  return request<LoginResponseType>(client, client.login, loginRequest, false);
};

export const registerApi = (
  email: string,
  password: string,
  fullName: string,
  captcha: string,
  code: string,
  refCodes: RefCodeTypes[],
  source?: string
): Promise<LoginResponseType> => {
  const client = authSharedService.getInstance();
  const registrationRequest = new AuthSharedModel.LoginRequest();

  registrationRequest.setToken(email);
  registrationRequest.setOptionalPassword(password);
  registrationRequest.setRealm(getRealm());
  registrationRequest.setDestinationRealm(getRealm());
  registrationRequest.setType(AuthHandlerType.EMAIL);
  registrationRequest.setUtcTimeZone(getTimeZone());
  registrationRequest.setOptionalVerificationCode(code);
  registrationRequest.setOptionalFullName(fullName);

  const attribution = composeAttribution();
  if (attribution) {
    registrationRequest.setOptionalAttribution(attribution);
  }

  const registrationCodes: Array<RegistrationCode> = composeRegistrationCodes(
    refCodes,
    getAttributionUtmPairs()
  );

  if (source) {
    const regCode = new RegistrationCode();
    regCode.setCode(source);
    regCode.setType(CodeType.A_B_TESTING_CODE);

    registrationCodes.push(regCode);
  }

  if (captcha) {
    registrationRequest.setOptionalReCaptchaResponse(captcha);
  }

  if (registrationCodes.length) {
    registrationRequest.setOptionalRegistrationCodesList(registrationCodes);
  }

  return request<LoginResponseType>(client, client.login, registrationRequest, false);
};

export const logoutApi = (token: string): Promise<void> => {
  const client = authSharedService.getInstance();
  const logoutRequest = new AuthSharedModel.LogoutRequest();

  logoutRequest.setToken(token);
  logoutRequest.setRealm(getRealm());

  return request<void>(client, client.logout, logoutRequest, true, false);
};

export const getInvitationInfoApi = (token: string) => {
  const client = authSharedService.getInstance();
  const tokenRequest = new google_protobuf_wrappers_pb.StringValue();

  tokenRequest.setValue(token);

  return request(client, client.getInvitationInfo, tokenRequest, false);
};

export const acceptInvitationApi = (
  fullName: string,
  password: string,
  token: string,
  captcha: string
): Promise<LoginResponseType> => {
  const client = authSharedService.getInstance();
  const acceptRequest = new AuthSharedModel.InvitationRequest();

  acceptRequest.setFullName(fullName);
  acceptRequest.setPassword(password);
  acceptRequest.setInviteToken(token);
  acceptRequest.setUtcTimeZone(getTimeZone());
  acceptRequest.setRealm(getRealm());

  if (captcha) {
    acceptRequest.setOptionalReCaptchaResponse(captcha);
  }

  return request<LoginResponseType>(client, client.acceptInvitation, acceptRequest, false);
};

function composeAttributionTouch(touch: AttributionTouchData): AttributionTouch {
  const protoTouch = new AttributionTouch();

  if (touch.source) protoTouch.setSource(touch.source);
  if (touch.medium) protoTouch.setMedium(touch.medium);
  if (touch.campaign) protoTouch.setCampaign(touch.campaign);
  if (touch.term) protoTouch.setTerm(touch.term);
  if (touch.content) protoTouch.setContent(touch.content);
  if (touch.referrer) protoTouch.setReferrer(touch.referrer);
  if (touch.landingPage) protoTouch.setLandingPage(touch.landingPage);
  if (touch.capturedAt) protoTouch.setCapturedAt(touch.capturedAt);
  if (touch.clickIds) {
    protoTouch.setClickIdsList(
      Object.entries(touch.clickIds).map(([name, value]) => {
        const pair = new Pair();
        pair.setName(name);
        pair.setValue(value);
        return pair;
      })
    );
  }

  return protoTouch;
}

/**
 * The persisted first/last-touch attribution as a proto message, or undefined when
 * nothing was captured (flag off, Semrush realm, storage blocked). See TRACK-004.
 */
function composeAttribution(): Attribution | undefined {
  const attribution = getAttribution();
  if (!attribution?.firstTouch && !attribution?.lastTouch) return undefined;

  const protoAttribution = new Attribution();
  if (attribution.firstTouch) {
    protoAttribution.setFirstTouch(composeAttributionTouch(attribution.firstTouch));
  }
  if (attribution.lastTouch) {
    protoAttribution.setLastTouch(composeAttributionTouch(attribution.lastTouch));
  }
  return protoAttribution;
}

function composeRegistrationCodes(refCodes: RefCodeTypes[], utmTagPairs: [string, string][]) {
  const registrationCodes: Array<RegistrationCode> = [];
  for (const refCode of refCodes) {
    if (refCode.key != null && refCode.key.length > 0) {
      const regCode = new RegistrationCode();
      regCode.setCode(refCode.key);
      regCode.setType(refCode.type);
      if (utmTagPairs?.length) {
        regCode.setTagsList(
          utmTagPairs.map(([key, value]) => {
            const pair = new Pair();

            pair.setName(key);
            pair.setValue(value);

            return pair;
          })
        );
      }

      registrationCodes.push(regCode);
    }
  }
  return registrationCodes;
}

export const loginByIntegrationApi = (
  code: string,
  refCodes: RefCodeTypes[],
  source?: string
): Promise<LoginResponseType> => {
  const client = authSharedService.getInstance();
  const loginRequest = new AuthSharedModel.LoginRequest();

  loginRequest.setToken(code);
  loginRequest.setRealm(getRealm());
  loginRequest.setDestinationRealm(getRealm());
  loginRequest.setType(AuthHandlerType.GOOGLE);
  loginRequest.setUtcTimeZone(getTimeZone());

  // Only meaningful on the register path; the backend ignores it on plain login.
  const attribution = composeAttribution();
  if (attribution) {
    loginRequest.setOptionalAttribution(attribution);
  }

  const registrationCodes: Array<RegistrationCode> = composeRegistrationCodes(
    refCodes,
    getAttributionUtmPairs()
  );

  if (source) {
    const regCode = new RegistrationCode();
    regCode.setCode(source);
    regCode.setType(CodeType.A_B_TESTING_CODE);

    registrationCodes.push(regCode);
  }

  if (registrationCodes.length) {
    loginRequest.setOptionalRegistrationCodesList(registrationCodes);
  }

  return request<LoginResponseType>(client, client.login, loginRequest, false);
};

export const showReCaptchaApi = (): Promise<ReCaptchaShowStatusType> => {
  const client = authSharedService.getInstance();
  const emptyRequest = new google_protobuf_empty_pb.Empty();

  return request<ReCaptchaShowStatusType>(client, client.showReCaptcha, emptyRequest, false);
};

export const sendVerificationCodeApi = (
  email: string,
  type: VerificationCodeTypeMap[keyof VerificationCodeTypeMap]
): Promise<void> => {
  const client = authSharedService.getInstance();
  const verificationRequest = new AuthSharedModel.VerificationCodeRequest();

  verificationRequest.setEmail(email);
  verificationRequest.setType(type);
  return request<void>(client, client.sendVerificationCode, verificationRequest, false);
};

export const changePasswordByTokenApi = (
  code: string,
  email: string,
  password: string
): Promise<LoginResponseType> => {
  const client = authSharedService.getInstance();
  const changePasswordByTokenRequest = new AuthSharedModel.ChangeForgotPasswordRequest();

  changePasswordByTokenRequest.setCode(code);
  changePasswordByTokenRequest.setPassword(password);
  changePasswordByTokenRequest.setEmail(email);
  changePasswordByTokenRequest.setRealm(getRealm());

  return request<LoginResponseType>(
    client,
    client.changePassword,
    changePasswordByTokenRequest,
    false
  );
};

export const getAppSumoAccessTokenApi = (code: string): Promise<AppSumoTokensResponseType> => {
  const client = authSharedService.getInstance();
  const codeRequest = new google_protobuf_wrappers_pb.StringValue();

  codeRequest.setValue(code);

  return request<AppSumoTokensResponseType>(
    client,
    client.getAppSumoAccessToken,
    codeRequest,
    false
  );
};
